/**
 * Person Drawer
 *
 * Slide-out panel for a single seat in the org structure. Opened when a person
 * (or vacant seat) row is clicked in OrgStructureTree. Detail + safe actions.
 *
 * Data comes from `organizationService.getPosition(positionId)` -> PositionDetail
 * (incumbent, reports_to, direct_reports, history). Reporting-line and direct-
 * report rows are clickable and re-open the drawer on that seat via `onOpenPerson`
 * (routed through the parent, which owns the selected position id — single source
 * of truth, so back/forward navigation just changes the prop and re-fetches).
 *
 * Write actions (transfer, reporting-line change, assign incumbent) are NOT
 * performed here — a local write would be reconciled away by the nightly F&O sync.
 * Instead the footer buttons DEEP-LINK into the HR-Ops "Find & Amend" Amendment
 * Slip (VITE_HR_OPS_APP_URL → /new-hire?tab=find&pn=…), which writes F&O + Sage
 * durably. Vacant seats deep-link to the New Hire (Create) tab.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, AlertCircle, Copy, Check, GitBranch, Users, Clock,
  Briefcase, ArrowRightLeft, UserPlus, ChevronRight, UserX, ChevronDown,
  ExternalLink, ArrowLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Sheet, SheetContent, SheetDescription, SheetFooter,
  SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import organizationService, {
  PositionDetail, PositionBrief, PositionStatus, type Position,
} from '@/services/api/organizationService';
import { AsyncSearchCombobox } from './AsyncSearchCombobox';
import { useUserPhoto } from '@/hooks/useUserPhoto';
import ReportingLinePanel from './ReportingLinePanel';
import PositionAssignmentPanel from './PositionAssignmentPanel';
import VacantReportingLinePanel from './VacantReportingLinePanel';

// Inline-mutation modes for the drawer body. `null` = the read-only detail view.
type DrawerMode = 'reporting' | 'vacant-reporting' | 'transfer' | 'assign' | null;

// =============================================================================
// HELPERS
// =============================================================================

const POSITION_STATUS_LABEL: Record<PositionStatus, string> = {
  ACTIVE: 'Active',
  VACANT: 'Vacant',
  FROZEN: 'Frozen',
  PENDING_APPROVAL: 'Pending',
  RETIRED: 'Retired',
};

// Dark-mode-safe badge tokens (the light-only maps elsewhere read poorly on the
// Sheet's bg-background in dark theme).
const POSITION_STATUS_STYLE: Record<PositionStatus, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  VACANT: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  FROZEN: 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300',
  PENDING_APPROVAL: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  RETIRED: 'bg-gray-100 text-gray-500 dark:bg-gray-500/20 dark:text-gray-400',
};

const DIRECT_REPORTS_CAP = 25;

// --- HR-Ops deep-linking -----------------------------------------------------
// Position mutations (assign incumbent, transfer, reporting-line change) are
// written through the HR-Ops "Find & Amend" Amendment Slip, which persists to
// F&O + Sage. A local write here would be reconciled away by the nightly F&O
// sync, so we deep-link out instead of holding half-built forms.
//
// VITE_HR_OPS_APP_URL is baked at build time (Dockerfile ARG / azure.yaml /
// docker-compose), e.g. "http://localhost:5174/hr-ops/". Worker Records lives at
// /new-hire; ?tab=find selects Find & Amend and ?pn=<PN> pre-searches +
// auto-opens that worker's slip. Vacant seats go to the New Hire (Create) tab.
const HR_OPS_APP_URL = (import.meta.env.VITE_HR_OPS_APP_URL as string | undefined)?.trim();

function hrOpsWorkerRecordsUrl(params: Record<string, string>): string | null {
  if (!HR_OPS_APP_URL) return null;
  const base = HR_OPS_APP_URL.replace(/\/+$/, '');
  const qs = new URLSearchParams(params).toString();
  return `${base}/new-hire${qs ? `?${qs}` : ''}`;
}

// Find & Amend deep-link for an existing worker (transfer / reporting-line change).
function hrOpsFindAndAmendUrl(pn?: string | null): string | null {
  const params: Record<string, string> = { tab: 'find' };
  if (pn) params.pn = pn;
  return hrOpsWorkerRecordsUrl(params);
}

// New-hire deep-link — filling a vacant seat is a hire.
function hrOpsHireUrl(): string | null {
  return hrOpsWorkerRecordsUrl({ tab: 'create' });
}

function openHrOps(url: string) {
  window.open(url, '_blank', 'noopener');
}

// Small secondary text-link into HR-Ops — the "full slip" fallback for users
// who lack the mutation role or want the complete drift Amendment Slip. Falls
// back to a toast hint when VITE_HR_OPS_APP_URL is unset in this build.
const HrOpsFallbackLink: React.FC<{
  url: string | null;
  label: string;
  fallbackMsg: string;
}> = ({ url, label, fallbackMsg }) => (
  <button
    type="button"
    onClick={() => (url ? openHrOps(url) : toast(fallbackMsg))}
    className="inline-flex items-center justify-center gap-1 rounded text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue/50"
  >
    {label}
    {url && <ExternalLink className="h-3 w-3 opacity-70" />}
  </button>
);

function getInitials(name?: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <span className="text-muted-foreground">{label}:</span>{' '}
    <span className="font-medium text-foreground">{children}</span>
  </div>
);

const SectionHeading: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
    {icon}
    {children}
  </h4>
);

// A clickable reporting-line / direct-report row (a related seat).
/**
 * Absentee approver: who acts as people leader / leave approver for this seat's
 * incumbent while the seat's Line parent is vacant. Line-derived by the nightly
 * seeding unless a decision is recorded here — recording writes the platform
 * override and the F&O absentee-approver hierarchy edge at once.
 */
const AbsenteeApproverPanel: React.FC<{
  detail: PositionDetail;
  onNavigate: (positionId: number) => void;
  onChanged: () => void;
}> = ({ detail, onNavigate, onChanged }) => {
  // 'own' edits this seat's own approver; 'reports' records ONE stand-in on every direct
  // report of a (vacant) seat — that is the edge the workflow actually reads.
  const [mode, setMode] = React.useState<'own' | 'reports' | null>(null);
  const [approver, setApprover] = React.useState<Position | null>(null);
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const override = detail.absentee_approver_override_fo_position_id;
  // The approver endpoints key on the F&O id: some seats carry a platform
  // code ("FO-005557") that F&O and the override lookup don't know.
  const seatFo = detail.fo_position_id || detail.position_code;
  const lineVacant = !!detail.reports_to && (!detail.reports_to.incumbent_name || detail.reports_to.status === 'VACANT');
  // A vacant seat's drawer first answers the question people actually ask of it — who
  // acts for its reports — before the seat's own approver (which only matters once filled).
  const seatVacant = !detail.incumbent || detail.status === 'VACANT';
  const actingCount = detail.acting_for_reports_count ?? 0;
  // The stand-in block only makes sense on a vacant seat that actually has reports.
  const activeReportCount = (detail.direct_reports ?? []).filter((r) => r.status !== 'RETIRED').length;
  const showReportsBlock = seatVacant && (activeReportCount > 0 || !!detail.acting_for_reports);

  // Same lookup as the reporting-line panels: search live seats, but an absentee
  // approver must be a FILLED seat (the workflow assigns the work item to its
  // incumbent's F&O user) and never the seat itself.
  const searchApprovers = React.useMemo(
    () => async (q: string) => {
      const positions = await organizationService.listPositions({ query: q, limit: 20, status: ['ACTIVE'] });
      return positions.filter((p) => p.position_code !== detail.position_code && !!p.incumbent_name);
    },
    [detail.position_code],
  );

  const reset = () => { setMode(null); setApprover(null); setReason(''); setProgress(null); };

  const submitOwn = async (target: string | null) => {
    setBusy(true); setError(null);
    try {
      const res = await organizationService.setAbsenteeApprover(seatFo, target, reason || undefined);
      if (!res.ok) { setError(res.error || res.edge_error || 'Could not record the absentee approver'); return; }
      reset();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const submitReports = async (target: string | null) => {
    setBusy(true); setError(null);
    try {
      const res = await organizationService.cascadeAbsenteeApprover(seatFo, target, reason || undefined, true);
      if (!res.ok) {
        setError(res.error || (res.failed?.length ? `Could not record on ${res.failed.join(', ')}` : 'Could not record the stand-in'));
        return;
      }
      const n = res.reports?.length ?? 0;
      if (res.job_id) {
        setProgress(`Recorded on ${n} direct report${n === 1 ? '' : 's'} — writing the F&O edges…`);
        for (let i = 0; i < 180; i += 1) {
          await new Promise((r) => setTimeout(r, 2000));
          const job = await organizationService.getLeaveHierarchyJob(res.job_id);
          setProgress(`${job.done ?? 0} of ${job.total ?? n} F&O edges written`);
          if (job.status === 'completed' || job.status === 'failed') {
            if (job.failed || job.error) {
              setError(`${job.failed ?? 0} edge(s) failed in F&O${job.error ? ` — ${job.error}` : ''}; the nightly reconcile retries them.`);
            }
            break;
          }
        }
      }
      reset();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const editor = (onSave: (code: string) => void) => (
    <div className="space-y-2 rounded-md border border-border p-2">
      <AsyncSearchCombobox<Position>
        search={searchApprovers}
        getKey={(p) => p.position_code}
        getPrimary={(p) => p.incumbent_name || 'Vacant'}
        getSecondary={(p) => `${p.title} · ${p.position_code}`}
        onSelect={setApprover}
        placeholder="Search active workers by name…"
        disabled={busy}
        emptyText="No filled seats match."
      />
      {approver && (
        <p className="text-xs text-muted-foreground">
          Selected: <span className="font-medium text-foreground">{approver.incumbent_name}</span> ({approver.title} · {approver.position_code})
        </p>
      )}
      <input
        className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
        placeholder="Reason (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex gap-2">
        <Button size="sm" disabled={busy || !approver} onClick={() => approver && onSave(approver.fo_position_id || approver.position_code)}>Save</Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => { reset(); setError(null); }}>Cancel</Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-2">
      {showReportsBlock && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
            Stand-in for this seat&apos;s {activeReportCount} direct report{activeReportCount === 1 ? '' : 's'}
            {actingCount > 0 && actingCount !== activeReportCount ? ` (${actingCount} filled)` : ''}
          </p>
          {detail.acting_for_reports ? (
            <RelatedSeatRow brief={detail.acting_for_reports} onClick={() => onNavigate(detail.acting_for_reports!.id)} />
          ) : (
            <p className="text-sm text-muted-foreground">None derived yet — the reports follow the next filled seat above.</p>
          )}
          <p className="text-xs text-muted-foreground">
            Recorded on each direct report — that is the edge that routes their leave. Set it once here and it cascades to all of them, vacant reports included.
          </p>
          {mode === 'reports' ? editor((code) => { void submitReports(code); }) : mode === null && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={busy} onClick={() => setMode('reports')}>
                {detail.acting_for_reports ? 'Change stand-in for all reports' : 'Set stand-in for all reports'}
              </Button>
              {detail.acting_for_reports && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Clear the recorded stand-in on every direct report of ${detail.position_code}? Their approver reverts to the next filled seat above.`)) {
                      void submitReports(null);
                    }
                  }}
                >
                  Clear reports&apos; decisions
                </Button>
              )}
            </div>
          )}
          {progress && <p className="text-xs text-muted-foreground">{progress}</p>}
          <p className="pt-2 text-xs font-medium text-muted-foreground">This seat&apos;s own approver (applies once the seat is filled)</p>
        </div>
      )}
      {detail.absentee_approver ? (
        <RelatedSeatRow brief={detail.absentee_approver} onClick={() => onNavigate(detail.absentee_approver!.id)} />
      ) : (
        <p className="text-sm text-muted-foreground">
          {lineVacant ? 'None yet — the Line manager seat is vacant, so leave requests from this seat have no approver.' : 'Follows the Line manager (derived nightly).'}
        </p>
      )}
      {override && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Recorded decision
          {/* Attributed server-side to the signed-in user; service/seed writes carry no person. */}
          {detail.absentee_approver_override_set_by && detail.absentee_approver_override_set_by.includes('@')
            ? ` by ${detail.absentee_approver_override_set_by.split('@')[0].replace(/\./g, ' ')}`
            : ''}
          {detail.absentee_approver_override_reason ? ` — ${detail.absentee_approver_override_reason}` : ''}
        </p>
      )}
      {mode === 'own' ? editor((code) => { void submitOwn(code); }) : mode === null && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={busy} onClick={() => setMode('own')}>
            {override ? 'Change' : seatVacant ? "Set this seat's own approver" : 'Set absentee approver'}
          </Button>
          {override && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => { void submitOwn(null); }}>
              Clear decision
            </Button>
          )}
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};

const RelatedSeatRow: React.FC<{
  brief: PositionBrief;
  onClick: () => void;
}> = ({ brief, onClick }) => {
  const vacant = !brief.incumbent_name || brief.status === 'VACANT';
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-md border border-border/60 px-2.5 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue/50"
    >
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarFallback
          className={cn(
            'text-[10px]',
            vacant
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
              : 'bg-heritage-blue/10 text-heritage-blue dark:bg-heritage-blue-400/20 dark:text-heritage-blue-100',
          )}
        >
          {vacant ? <UserX className="h-3.5 w-3.5" /> : getInitials(brief.incumbent_name)}
        </AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-foreground">
          {vacant ? 'Vacant' : brief.incumbent_name}
        </span>
        <span className="block truncate text-xs text-muted-foreground">{brief.title}</span>
      </span>
      <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{brief.position_code}</span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
};

// =============================================================================
// PROPS
// =============================================================================

export interface PersonDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  positionId: number | null;
  /**
   * Re-open the drawer on a related seat. userId is `null` when navigating from
   * a PositionBrief (manager / direct report) that carries no incumbent user id —
   * the drawer re-fetches by positionId, so the id is not needed to render.
   */
  onOpenPerson?: (userId: number | null, positionId: number) => void;
  /**
   * Called after a mutation from this drawer commits (reporting-line change /
   * assign / transfer), so the parent can refresh the org trees + positions.
   * Fires after the drawer's own refetch, by which point the occupancy re-read
   * has settled, so the parent reads the fresh projection.
   */
  onChanged?: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const PersonDrawer: React.FC<PersonDrawerProps> = ({
  open, onOpenChange, positionId, onOpenPerson, onChanged,
}) => {
  const [detail, setDetail] = useState<PositionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showAllReports, setShowAllReports] = useState(false);
  // Inline-mutation mode. Reset whenever the drawer opens on a (new) seat.
  const [mode, setMode] = useState<DrawerMode>(null);
  // Background refetch after a mutation — distinct from `loading` (initial fetch)
  // so a post-mutation reload doesn't unmount the detail behind the spinner.
  const [refetching, setRefetching] = useState(false);

  useEffect(() => {
    if (!open || positionId == null) return;
    let active = true;
    setLoading(true);
    setError(null);
    setDetail(null);
    setShowAllReports(false);
    setMode(null);
    organizationService
      .getPosition(positionId)
      .then((d) => { if (active) setDetail(d); })
      .catch((err) => {
        console.error('Failed to load position detail', positionId, err);
        if (active) setError('Failed to load this seat. Please try again.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, positionId]);

  // Silent refetch of the seat (after an inline mutation) — keeps the current
  // detail on screen while the fresh projection loads (uses `refetching`, NOT
  // `loading`, so the whole drawer doesn't flash the full-screen spinner), and
  // preserves the detail on a refetch error rather than replacing it with the
  // error screen.
  const reload = useCallback(() => {
    if (positionId == null) return;
    setRefetching(true);
    organizationService
      .getPosition(positionId)
      .then(setDetail)
      .catch(() => toast.error('Couldn’t refresh this seat — reopen to see the latest.'))
      .finally(() => setRefetching(false));
  }, [positionId]);

  // Leave the mutation view back to the detail. Always refetch: a mutation may
  // have committed (the panel's own Done also lands here), so any exit from the
  // mode view must show the fresh projection, never the pre-mutation snapshot.
  const exitMode = useCallback(() => {
    setMode(null);
    reload();
  }, [reload]);

  // A panel finished: leave the mutation view, refetch, and confirm. The saga
  // wrote F&O durably and (best-effort) already re-read the affected seats'
  // occupancy into the projection before calling this, so the refetch below
  // sees the fresh state; on a refresh failure it self-heals on the nightly sync.
  const handlePanelDone = useCallback(() => {
    setMode(null);
    reload();
    onChanged?.(); // refresh the parent's trees + positions off the fresh projection
    toast.success('Change submitted — F&O updated.');
  }, [reload, onChanged]);

  const isVacant = !!detail && (detail.is_vacant || detail.status === 'VACANT' || !detail.incumbent);
  const incumbentUserId = detail && !isVacant ? detail.incumbent?.id ?? null : null;
  // Hook must run every render (before any early return in the body).
  const photo = useUserPhoto(incumbentUserId);

  const campus = detail?.campus_name || detail?.campus_code || '';
  const department = detail?.organizational_unit?.name || detail?.functional_area || '';

  const copyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      toast.success('Email copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Couldn’t copy to clipboard');
    }
  };

  const navigateToSeat = (pid: number) => {
    if (onOpenPerson) onOpenPerson(null, pid);
  };

  const reports = detail?.direct_reports ?? [];
  const shownReports = showAllReports ? reports : reports.slice(0, DIRECT_REPORTS_CAP);

  // HR-Ops deep-links for the footer mutation buttons. Null when
  // VITE_HR_OPS_APP_URL is unset in the build → buttons degrade to a toast.
  const incumbentPn = detail?.incumbent?.employee_id ?? null;
  const findAmendUrl = hrOpsFindAndAmendUrl(incumbentPn);
  const hireUrl = hrOpsHireUrl();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
        {loading && (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6">
            <SheetTitle className="sr-only">Loading seat</SheetTitle>
            <SheetDescription className="sr-only">Loading the selected position.</SheetDescription>
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading seat…</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <SheetTitle className="sr-only">Couldn’t load seat</SheetTitle>
            <SheetDescription className="sr-only">{error}</SheetDescription>
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-sm text-foreground">{error}</p>
            {positionId != null && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Re-trigger the load effect by nudging open state is overkill;
                  // just refetch inline.
                  setLoading(true);
                  setError(null);
                  organizationService
                    .getPosition(positionId)
                    .then(setDetail)
                    .catch(() => setError('Failed to load this seat. Please try again.'))
                    .finally(() => setLoading(false));
                }}
              >
                Retry
              </Button>
            )}
          </div>
        )}

        {!loading && !error && detail && (
          <>
            {/* Header */}
            <SheetHeader className="space-y-0 border-b border-border p-6 pb-4 text-left">
              <div className="flex items-start gap-3">
                <Avatar className="h-12 w-12 shrink-0">
                  {photo && <AvatarImage src={photo} alt={detail.incumbent_name || ''} />}
                  <AvatarFallback
                    className={cn(
                      'text-sm',
                      isVacant
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                        : 'bg-heritage-blue/10 text-heritage-blue dark:bg-heritage-blue-400/20 dark:text-heritage-blue-100',
                    )}
                  >
                    {isVacant ? <UserX className="h-5 w-5" /> : getInitials(detail.incumbent_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <SheetTitle className="truncate text-lg">
                    {isVacant ? 'Vacant seat' : detail.incumbent_name}
                  </SheetTitle>
                  <SheetDescription className="truncate">{detail.title}</SheetDescription>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <Badge className={cn('text-[10px]', POSITION_STATUS_STYLE[detail.status])}>
                      {POSITION_STATUS_LABEL[detail.status]}
                    </Badge>
                    <span className="font-mono text-[11px] text-muted-foreground">{detail.position_code}</span>
                    {refetching && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" /> Refreshing…
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </SheetHeader>

            {/* Inline-mutation view — replaces the detail body + footer. */}
            {mode && (
              <div className="flex-1 space-y-4 p-6">
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-2"
                  onClick={exitMode}
                >
                  <ArrowLeft className="mr-1 h-4 w-4" /> Back
                </Button>

                {mode === 'reporting' && incumbentPn && (
                  <ReportingLinePanel
                    incumbentPn={incumbentPn}
                    incumbentName={detail.incumbent_name}
                    currentManagerName={
                      detail.reports_to?.incumbent_name ||
                      (detail.reports_to ? `${detail.reports_to.title} (vacant)` : undefined)
                    }
                    seatPositionCode={detail.position_code}
                    seatFoPositionId={detail.fo_position_id || detail.position_code}
                    recordedStandIn={
                      detail.absentee_approver_override_fo_position_id
                        ? { name: detail.absentee_approver?.incumbent_name }
                        : null
                    }
                    onDone={handlePanelDone}
                    hrOpsUrl={findAmendUrl}
                    onOpenHrOps={openHrOps}
                  />
                )}

                {mode === 'transfer' && (
                  <PositionAssignmentPanel
                    mode="transfer"
                    seatPositionCode={detail.position_code}
                    seatPositionTitle={detail.title}
                    incumbentPn={incumbentPn ?? undefined}
                    incumbentName={detail.incumbent_name ?? undefined}
                    onDone={handlePanelDone}
                    hrOpsUrl={findAmendUrl}
                    onOpenHrOps={openHrOps}
                  />
                )}

                {mode === 'assign' && (
                  <PositionAssignmentPanel
                    mode="assign"
                    seatPositionCode={detail.position_code}
                    seatPositionTitle={detail.title}
                    onDone={handlePanelDone}
                    hrOpsUrl={hireUrl}
                    onOpenHrOps={openHrOps}
                  />
                )}

                {mode === 'vacant-reporting' && (
                  <VacantReportingLinePanel
                    seatPositionCode={detail.position_code}
                    seatTitle={detail.title}
                    currentParentName={detail.reports_to?.incumbent_name}
                    onDone={handlePanelDone}
                  />
                )}
              </div>
            )}

            {/* Body */}
            {!mode && (
            <div className="flex-1 space-y-5 p-6">
              {isVacant && (
                <div className="rounded-md border border-dashed border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                  This seat has no active incumbent.
                </div>
              )}

              {/* Employment (person) — only when filled */}
              {!isVacant && detail.incumbent && (
                <section className="space-y-2">
                  <SectionHeading icon={<Users className="h-3.5 w-3.5" />}>Employment</SectionHeading>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <Field label="Campus">{campus || '—'}</Field>
                    <Field label="Department">{department || '—'}</Field>
                    <Field label="Employee #">{detail.incumbent.employee_id || '—'}</Field>
                    <Field label="Job title">{detail.incumbent.job_title || '—'}</Field>
                    <div className="col-span-2 min-w-0">
                      <span className="text-muted-foreground">Email:</span>{' '}
                      {detail.incumbent.email ? (
                        <button
                          type="button"
                          onClick={() => copyEmail(detail.incumbent!.email)}
                          className="inline-flex max-w-full items-center gap-1.5 rounded font-medium text-foreground hover:text-heritage-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue/50"
                          title="Copy email"
                        >
                          <span className="truncate">{detail.incumbent.email}</span>
                          {copied ? (
                            <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                          ) : (
                            <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          )}
                        </button>
                      ) : (
                        <span className="font-medium text-foreground">—</span>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {/* Position (seat) */}
              <section className="space-y-2">
                <SectionHeading icon={<Briefcase className="h-3.5 w-3.5" />}>Position</SectionHeading>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {isVacant && <Field label="Campus">{campus || '—'}</Field>}
                  {isVacant && <Field label="Department">{department || '—'}</Field>}
                  <Field label="Type">{detail.position_type}</Field>
                  <Field label="Grade">{detail.grade_level || '—'}</Field>
                  {detail.role_code && <Field label="Role">{detail.role_code}</Field>}
                  <Field label="FTE">{detail.fte}</Field>
                  {detail.cost_center && <Field label="Cost centre">{detail.cost_center}</Field>}
                  {/* F&O financial-dimension codes (budget codes) — distinct from the
                      physical campus above. */}
                  {detail.dimension_campus && <Field label="Fin. campus">{detail.dimension_campus}</Field>}
                  {detail.dimension_department && <Field label="Fin. department">{detail.dimension_department}</Field>}
                  {detail.dimension_faculty && <Field label="Faculty">{detail.dimension_faculty}</Field>}
                  {detail.effective_date && (
                    <Field label="Effective">{new Date(detail.effective_date).toLocaleDateString()}</Field>
                  )}
                  {detail.fo_position_id && <Field label="F&O position">{detail.fo_position_id}</Field>}
                </div>
              </section>

              <hr className="border-border" />

              {/* Reporting line */}
              <section className="space-y-2">
                <SectionHeading icon={<GitBranch className="h-3.5 w-3.5" />}>Reporting line</SectionHeading>
                {detail.reports_to ? (
                  <RelatedSeatRow brief={detail.reports_to} onClick={() => navigateToSeat(detail.reports_to!.id)} />
                ) : (
                  <p className="text-sm text-muted-foreground">No manager on record.</p>
                )}
              </section>

              {/* Absentee approver — acts as people leader / leave approver when the Line parent is vacant */}
              <section className="space-y-2">
                <SectionHeading icon={<GitBranch className="h-3.5 w-3.5" />}>Absentee approver</SectionHeading>
                <AbsenteeApproverPanel
                  detail={detail}
                  onNavigate={navigateToSeat}
                  onChanged={handlePanelDone}
                />
              </section>

              {/* Direct reports */}
              <section className="space-y-2">
                <SectionHeading icon={<Users className="h-3.5 w-3.5" />}>
                  Direct reports
                  {reports.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">{reports.length}</Badge>
                  )}
                </SectionHeading>
                {reports.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No direct reports.</p>
                ) : (
                  <div className="space-y-1.5">
                    {shownReports.map((r) => (
                      <RelatedSeatRow key={r.id} brief={r} onClick={() => navigateToSeat(r.id)} />
                    ))}
                    {reports.length > DIRECT_REPORTS_CAP && !showAllReports && (
                      <button
                        type="button"
                        onClick={() => setShowAllReports(true)}
                        className="flex w-full items-center justify-center gap-1 rounded-md py-1.5 text-xs font-medium text-heritage-blue hover:bg-muted dark:text-heritage-blue-100"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                        Show all {reports.length}
                      </button>
                    )}
                  </div>
                )}
              </section>

              {/* Recent history */}
              {detail.history && detail.history.length > 0 && (
                <>
                  <hr className="border-border" />
                  <section className="space-y-2">
                    <SectionHeading icon={<Clock className="h-3.5 w-3.5" />}>Recent history</SectionHeading>
                    <div className="max-h-[220px] space-y-2 overflow-y-auto">
                      {detail.history.slice(0, 15).map((h) => (
                        <div key={h.id} className="flex items-start gap-2 border-l-2 border-border pl-3 text-sm">
                          <Clock className="mt-1 h-3 w-3 shrink-0 text-muted-foreground" />
                          <div className="min-w-0">
                            <span className="font-medium text-foreground">{h.change_type}</span>
                            {h.change_description && (
                              <span className="text-muted-foreground"> · {h.change_description}</span>
                            )}
                            <div className="text-xs text-muted-foreground">
                              {new Date(h.effective_date || h.created_at).toLocaleDateString()}
                              {h.change_source && <span> · {h.change_source}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                </>
              )}
            </div>
            )}

            {/* Actions — inline mutations via the saga API, HR-Ops as fallback. */}
            {!mode && (
            <SheetFooter className="flex-col gap-2 border-t border-border p-4 sm:flex-col sm:space-x-0">
              {isVacant ? (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button className="w-full sm:flex-1" onClick={() => setMode('assign')}>
                      <UserPlus className="mr-1.5 h-4 w-4" /> Assign someone
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full sm:flex-1"
                      onClick={() => setMode('vacant-reporting')}
                    >
                      <GitBranch className="mr-1.5 h-4 w-4" /> Set reporting line
                    </Button>
                  </div>
                  <div className="flex justify-center pt-0.5">
                    <HrOpsFallbackLink
                      url={hireUrl}
                      label="Open in HR-Ops"
                      fallbackMsg="Fill this seat in the HR-Ops app → Worker Records → Create."
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      className="w-full sm:flex-1"
                      onClick={() => setMode('reporting')}
                    >
                      <GitBranch className="mr-1.5 h-4 w-4" /> Change reporting line
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full sm:flex-1"
                      onClick={() => setMode('transfer')}
                    >
                      <ArrowRightLeft className="mr-1.5 h-4 w-4" /> Transfer
                    </Button>
                  </div>
                  <div className="flex justify-center pt-0.5">
                    <HrOpsFallbackLink
                      url={findAmendUrl}
                      label="Open full slip in HR-Ops"
                      fallbackMsg="Amend this worker in the HR-Ops app → Worker Records → Find & Amend."
                    />
                  </div>
                </>
              )}
              <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)}>Close</Button>
            </SheetFooter>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default PersonDrawer;
